import React, {
    InputHTMLAttributes,
    DetailedHTMLProps,
    forwardRef,
    FC,
} from 'react'
import { createUseStyles, useTheme } from 'react-jss'
import { colors } from '../../../theme/palette'

type RuleNames = 'label' | 'input' | 'container'

type InputProps = {
    isChecked: boolean
    label: string
    reversed?: boolean
    labelStyle?: React.CSSProperties
} & Omit<
    DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
    'size'
>

interface StyleProps {
    isChecked: boolean
    reversed: boolean
}

const useStyles = createUseStyles<RuleNames, StyleProps, Jss.Theme>({
    container: {
        height: '1.5rem',
        display: 'flex',
        alignItems: 'center',
        fontSize: '1rem',
    },
    input: {
        position: 'absolute',
        opacity: 0,
        '& + label:after': {
            content: 'none',
        },
        '&:checked + label:after': {
            content: '""',
        },
        '&:focus + label::before': {
            outline: `${colors.green} auto 5px`,
        },
    },
    label: {
        position: 'relative',
        display: 'inline-block',
        paddingLeft: '2.125rem',
        paddingRight: ({ reversed }) => (reversed ? '2.125rem' : 0),
        height: '1.5rem',
        paddingTop: '.125rem',
        color: colors.black,
        cursor: 'pointer',
        '&:before, &:after': {
            position: 'absolute',
            content: '""',
            display: 'inline-block',
            borderRadius: '.25rem',
            backgroundColor: ({ isChecked }) =>
                isChecked ? colors.green : colors.white,
        },
        '&:before': {
            height: '1.5rem',
            width: '1.5rem',
            border: `1px solid ${colors.green}`,
            left: ({ reversed }) => (!reversed ? 0 : 'auto'),
            right: ({ reversed }) => (reversed ? 0 : 'auto'),
            top: '0',
        },
        '&:after': {
            height: '0.5rem',
            width: '1rem',
            borderLeft: `0.2rem solid ${colors.white}`,
            borderBottom: `0.2rem solid ${colors.white}`,
            borderRadius: '0',
            transform: 'rotate(-45deg)',
            left: ({ reversed }) => (!reversed ? '0.25rem' : 'auto'),
            right: ({ reversed }) => (!reversed ? 'auto' : '4px'),
            top: '0.4rem',
        },
    },
})

const Checkbox: FC<InputProps> = forwardRef<HTMLInputElement, InputProps>(
    (
        {
            label,
            required,
            isChecked,
            id,
            reversed = false,
            labelStyle,
            ...props
        },
        ref
    ) => {
        const theme = useTheme<Jss.Theme>()
        const classes = useStyles({ ...{ isChecked, reversed }, theme })
        return (
            <div className={classes.container}>
                <input
                    type="checkbox"
                    className={classes.input}
                    id={id || label}
                    checked={isChecked}
                    {...props}
                    ref={ref}
                    required={required}
                />
                <label
                    htmlFor={id || label}
                    className={classes.label}
                    style={labelStyle}
                >
                    {label}
                </label>
            </div>
        )
    }
)

export default Checkbox
